import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tag, CheckCircle2, XCircle } from "lucide-react";

export default function Signup() {
    const { signup } = useAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const codeFromUrl = searchParams.get("code") || "";
    const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", campaign_code: codeFromUrl.toUpperCase() });
    const [busy, setBusy] = useState(false);
    // Campaign-code preview state — { code, label, grant_plan, discount_pct, months } if valid, or {error} if invalid.
    const [codePreview, setCodePreview] = useState(null);

    // Whenever the URL provides a code, validate it against the backend so
    // the user sees "You'll get 50% off Pro for 3 months" BEFORE hitting
    // submit. Debounced to 400ms to avoid hammering on partial edits.
    useEffect(() => {
        const code = (form.campaign_code || "").trim().toUpperCase();
        if (!code) { setCodePreview(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/campaign-codes/${encodeURIComponent(code)}`);
                setCodePreview(data);
            } catch (e) {
                setCodePreview({ error: formatApiError(e) });
            }
        }, 400);
        return () => clearTimeout(t);
    }, [form.campaign_code]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form, role: "employer" };
            // Only send campaign_code if it's non-empty AND validated. This
            // avoids spamming the backend with pending-typing codes.
            if (!form.campaign_code || codePreview?.error) delete payload.campaign_code;
            const u = await signup(payload);
            toast.success(`Welcome, ${u.name}. Your company is set up.`);
            nav(u?.onboarding_status === "plans_pending" ? "/onboarding/plans" : "/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-foreground text-white">
                <Link to="/" className="flex items-center gap-2" data-testid="back-home">
                    <BrandLogo size={28} />
                    <span className="font-display font-black text-lg">{branding.short_name}</span>
                </Link>
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#7faaff] font-mono mb-4">Get started</div>
                    <h2 className="font-display font-black text-4xl leading-tight">
                        Your company.<br />Your recruiters.<br />Your <span className="text-[#7faaff]">candidate DB</span>.
                    </h2>
                    <ul className="mt-10 space-y-3 text-sm text-white/70">
                        <li>· AI parsing and scoring on every resume</li>
                        <li>· Invite unlimited recruiters in seconds</li>
                        <li>· Bulk upload, deduped by email automatically</li>
                        <li>· No credit card required</li>
                    </ul>
                </div>
                <div className="text-xs font-mono text-white/40">Phase 1 · Roadmap continues</div>
            </div>
            <div className="flex items-center justify-center p-5 sm:p-8 bg-background">
                <div className="w-full max-w-md py-6">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-2">Create company account</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl mb-6 sm:mb-8">Let's set up<br />your workspace.</h1>
                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Your name</Label>
                            <Input required value={form.name} onChange={set("name")} className="rounded-none mt-2 h-11" data-testid="signup-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Company name</Label>
                            <Input required value={form.company_name} onChange={set("company_name")} className="rounded-none mt-2 h-11" data-testid="signup-company" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Work email</Label>
                            <Input type="email" required value={form.email} onChange={set("email")} className="rounded-none mt-2 h-11" data-testid="signup-email" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Password</Label>
                            <Input type="password" required minLength={6} value={form.password} onChange={set("password")} className="rounded-none mt-2 h-11" data-testid="signup-password" />
                            <div className="text-xs text-muted-foreground mt-1">Min 6 characters.</div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
                                <Tag className="w-3 h-3" /> Promo code <span className="normal-case font-normal text-muted-foreground text-[10px]">(optional)</span>
                            </Label>
                            <Input
                                type="text"
                                value={form.campaign_code}
                                onChange={(e) => setForm((f) => ({ ...f, campaign_code: e.target.value.toUpperCase() }))}
                                placeholder="e.g. LAUNCH50"
                                className="rounded-none mt-2 h-11 uppercase font-mono"
                                data-testid="signup-campaign-code"
                            />
                            {codePreview && !codePreview.error && (
                                <div className="mt-2 flex items-start gap-1.5 text-xs text-emerald-700" data-testid="signup-code-valid">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div>
                                        <b className="font-mono">{codePreview.code}</b> applied.
                                        {codePreview.grant_plan && <> You'll get the <b className="uppercase">{codePreview.grant_plan}</b> plan</>}
                                        {codePreview.discount_pct > 0 && <> at <b>{codePreview.discount_pct}% off</b> for {codePreview.months} month{codePreview.months === 1 ? "" : "s"}</>}
                                        .
                                        {codePreview.expires_at && (() => {
                                            const daysLeft = Math.floor((new Date(codePreview.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
                                            if (daysLeft < 0) return null;
                                            return (
                                                <span className={`ml-1 font-mono ${daysLeft <= 3 ? "text-rose-700 font-bold" : "text-amber-700"}`} data-testid="signup-code-countdown">
                                                    {daysLeft === 0 ? "Expires today!" : `Ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                                                </span>
                                            );
                                        })()}
                                        {codePreview.redemptions_remaining !== null && codePreview.redemptions_remaining <= 10 && (
                                            <span className="ml-1 text-rose-700 font-mono">
                                                Only {codePreview.redemptions_remaining} spot{codePreview.redemptions_remaining === 1 ? "" : "s"} left!
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {codePreview?.error && form.campaign_code && (
                                <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-700" data-testid="signup-code-invalid">
                                    <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{codePreview.error}</span>
                                </div>
                            )}
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11" data-testid="signup-submit">
                            {busy ? "Creating..." : "Create workspace →"}
                        </Button>
                    </form>
                    <div className="mt-8 text-sm text-muted-foreground">
                        Already on {branding.short_name}?{" "}
                        <Link to="/login" className="text-ikb font-medium hover:underline" data-testid="goto-login">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
